import {
  shouldKeepMicrophoneLive,
  DELIVERY_CHANNELS,
  RESPONDER_PHASES,
  RESPONSE_MODES,
  VOICE_RECORDING_STATUSES,
  type ResponderCapabilities,
  type ResponderPhase,
  type ResponderSessionState,
  type ResponseMode,
  type ThreadKind,
  type VoiceRecordingStatus,
} from '@/shared-responder-v2-core';

/**
 * `Failed` is deliberately absent. A lost device leaves the port failed while
 * the question still wants the microphone, so re-arming from here re-entered
 * `getUserMedia` on every status change and spun. Recovery is the block screen's
 * retry button instead — one gesture, one attempt.
 */
const STARTABLE_VOICE_STATUSES: ReadonlySet<string> = new Set([
  VOICE_RECORDING_STATUSES.Idle,
]);

const LIVE_VOICE_STATUSES: ReadonlySet<string> = new Set([
  VOICE_RECORDING_STATUSES.Requesting,
  VOICE_RECORDING_STATUSES.Listening,
]);

/**
 * Whether the microphone belongs open for the question on screen.
 *
 * A contact-info question — or an open-ended one the customer pinned to text —
 * still keeps it live inside a voice session, because the responder may go back
 * to speaking on the very next question and a mic that has to be re-granted
 * mid-survey is a mic the responder gives up on. `shouldKeepMicrophoneLive`
 * already encodes exactly that rule for the composer, and reusing it is what
 * stops the two from drifting.
 *
 * `isScreenPending` sequences the two permission prompts. `getDisplayMedia`
 * requires the click's transient activation and consumes it, while `getUserMedia`
 * requires none at all — confirmed against Blink, which checks activation only on
 * `getDisplayMedia` and `selectAudioOutput`. So the share is asked for from the
 * gesture and the microphone from the continuation. Asking for both at once put
 * the microphone request behind the share picker, where the responder could not
 * see it to answer it, and the session then waited out its whole fail-open before
 * showing the question.
 *
 * `sessionMode` is the project's own mode rather than the question's resolved
 * one, and the distinction is the whole point: every text project has an
 * open-ended question, so reading the question's mode here made "keep it live"
 * true for all of them and prompted a text-only responder for their microphone.
 */
export function shouldCaptureVoice(args: {
  readonly phase: ResponderPhase;
  readonly mode: ResponseMode;
  readonly kind: ThreadKind | null;
  readonly sessionMode: ResponseMode;
  /** True while the screen share is still unsettled — asked for, refused, or waited out. */
  readonly isScreenPending: boolean;
}): boolean {
  if (args.isScreenPending) {
    return false;
  }

  if (args.phase !== RESPONDER_PHASES.Question) {
    return false;
  }

  if (args.mode === RESPONSE_MODES.Voice) {
    return true;
  }

  return (
    args.kind !== null &&
    shouldKeepMicrophoneLive({ mode: args.sessionMode, kind: args.kind })
  );
}

export function shouldStartVoiceCapture(args: {
  readonly isCapturing: boolean;
  readonly status: VoiceRecordingStatus;
}): boolean {
  return args.isCapturing && STARTABLE_VOICE_STATUSES.has(args.status);
}

export function shouldStopVoiceCapture(args: {
  readonly isCapturing: boolean;
  readonly status: VoiceRecordingStatus;
}): boolean {
  return !args.isCapturing && LIVE_VOICE_STATUSES.has(args.status);
}

/**
 * Where in the recorder's own timeline the current answer began. The recorder
 * runs across the whole session, so a cut names a position rather than a
 * length — and a question opened before the microphone did starts at zero
 * rather than at a negative offset.
 */
export function resolveAnswerCutOffsetMs(args: {
  readonly captureStartedAtMs: number | null;
  readonly questionStartedAtMs: number | null;
}): number {
  if (args.captureStartedAtMs === null || args.questionStartedAtMs === null) {
    return 0;
  }

  return Math.max(0, args.questionStartedAtMs - args.captureStartedAtMs);
}

export function isSpokenSession(
  capabilities: Pick<
    ResponderCapabilities,
    'projectInteractionType' | 'deliveryChannel'
  >
): boolean {
  return (
    capabilities.projectInteractionType === RESPONSE_MODES.Voice ||
    capabilities.deliveryChannel === DELIVERY_CHANNELS.HandsFree
  );
}

/**
 * Whether the session cannot proceed without the microphone, which is what
 * separates a refusal that must stop the session from one the composer can
 * absorb with a "type instead" notice. `/manager-embed` has `allowVoice` false
 * and therefore never requires it, however the project was authored.
 *
 * A relayed session never requires it either: the words are recognised on the
 * phone, so this page has nothing to ask for and the block screen would be a
 * wall across a session that is working.
 */
export function isMicrophoneRequiredForSession(args: {
  readonly state: ResponderSessionState;
  /** True where the responder speaks into a phone that relays the words in. */
  readonly isMicrophoneRemote: boolean;
}): boolean {
  if (args.isMicrophoneRemote || !args.state.capabilities.allowVoice) {
    return false;
  }

  return isSpokenSession(args.state.capabilities);
}
